import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import {
  contarPendientes,
  generarCodigoProveedor,
  listarProveedoresLocal,
  listarProductos,
  listarProveedores,
  contarProductosDeProveedor,
  obtenerUltimosErroresSync,
  siguienteNumeroProducto,
  sincronizarAhora,
  listarProductosLocalDeProveedor,
  actualizarNombreProveedor,
  eliminarProveedor,
} from '../../../application/casos-de-uso'
import { SesionCapturaService } from '../../../application/sesion-captura'
import { obtenerPrefijoPersona, guardarPrefijoPersona } from '../../../infrastructure/offline/preferencias'
import { AuthService, sesionSupabaseReal } from '../../../infrastructure/auth/auth.service'
import { conTimeout } from '../../../infrastructure/util/con-timeout'
import { EVENTO_SINCRONIZADO } from '../../../infrastructure/offline/sincronizador'
import type { ProveedorPendiente } from '../../../infrastructure/offline/db'
import { etiquetaProveedor } from '../../../domain/entidades'
import { IndicadorVersionComponent } from '../../components/indicador-version'
import { TarjetaProveedorComponent } from '../../components/tarjeta-proveedor'

const PREFIJOS = ['A', 'J', 'W', 'T', 'C']

interface ProductoDetalleItem {
  id: string
  mueble: string
  telas: number
  sincronizado: boolean
  telasDetalle: { id: string; muestrario: string; colores: string; tipo: string }[]
}

/** Pantalla movil de proveedores, prefijo de captura y estado de sincronizacion. */
@Component({
  selector: 'app-inicio',
  standalone: true,
  imports: [FormsModule, IndicadorVersionComponent, TarjetaProveedorComponent],
  templateUrl: './inicio.html',
})
export class InicioComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router)
  private readonly auth = inject(AuthService)
  private readonly sesion = inject(SesionCapturaService)

  readonly perfil = this.auth.perfil
  readonly sesionSupabaseReal = sesionSupabaseReal
  readonly prefijos = PREFIJOS

  readonly prefijo = signal(obtenerPrefijoPersona())
  readonly proveedores = signal<ProveedorPendiente[]>([])
  readonly conteos = signal<Record<string, number>>({})
  readonly pendientes = signal(0)
  readonly sincronizando = signal(false)
  readonly cerrandoSesion = signal(false)
  readonly errores = signal<string[]>([])
  readonly verErrores = signal(false)
  readonly detalleProveedor = signal<string | null>(null)
  /** "PROV-A-009 · AOYUNGTF" para el título del modal — detalleProveedor sigue siendo solo el código. */
  readonly detalleProveedorEtiqueta = computed(() => {
    const codigo = this.detalleProveedor()
    if (!codigo) return ''
    return etiquetaProveedor(codigo, this.proveedores().find((p) => p.codigo === codigo)?.nombre)
  })
  readonly detalleProductos = signal<ProductoDetalleItem[]>([])
  readonly detalleAbierto = signal(false)
  /** Objeto completo del proveedor del modal "Productos de..." -- para <app-tarjeta-proveedor>. */
  readonly detalleProveedorObjeto = computed(
    () => this.proveedores().find((p) => p.codigo === this.detalleProveedor()) ?? null,
  )

  /** Acceso rapido a la tarjeta desde la lista, sin pasar por "Productos de..." -- pedido 2026-09-14. */
  readonly tarjetaRapida = signal<ProveedorPendiente | null>(null)

  /** Edición de nombre de proveedor — pedido desde la feria, 2026-09-05. */
  readonly editandoProveedor = signal<ProveedorPendiente | null>(null)
  readonly nombreEditado = signal('')
  readonly guardandoNombre = signal(false)

  /** Eliminar proveedor (cascada) — pedido desde la feria, 2026-09-05. */
  readonly borrandoProveedor = signal<ProveedorPendiente | null>(null)
  readonly eliminando = signal(false)

  // Refrescamos la pantalla cuando vuelve la conexion o termina la sincronizacion.
  private readonly alVolverSenal = () => void this.recargar()
  private readonly alTerminarSync = () => void this.recargar()

  ngOnInit(): void {
    void this.recargar()
    window.addEventListener('online', this.alVolverSenal)
    window.addEventListener(EVENTO_SINCRONIZADO, this.alTerminarSync)
  }

  ngOnDestroy(): void {
    window.removeEventListener('online', this.alVolverSenal)
    window.removeEventListener(EVENTO_SINCRONIZADO, this.alTerminarSync)
  }

  async recargar(): Promise<void> {
    // Acotamos la consulta remota para priorizar datos locales sin bloquear la pantalla.
    const [locales, productosRemotos, proveedoresRemotos, pend, errs] = await Promise.all([
      listarProveedoresLocal(),
      conTimeout(listarProductos(), 2500, []),
      conTimeout(listarProveedores(), 2500, []),
      contarPendientes(),
      obtenerUltimosErroresSync(),
    ])
    this.errores.set(errs)
    const prefijo = this.prefijo()
    // Si el nombre se editó desde el dashboard, el celular no se entera solo
    // (nunca vuelve a preguntarle nada a Supabase para esta lista) — mostramos
    // el nombre remoto en cuanto el proveedor ya está sincronizado. Si todavía
    // hay una edición local pendiente de subir, priorizamos esa (más reciente
    // que cualquier cosa que hayamos traído recién de Supabase).
    const nombreRemotoPorCodigo = new Map(proveedoresRemotos.map((p) => [p.codigo, p.nombre]))
    const filtrados = locales
      .filter((p) => p.codigo.startsWith(`PROV-${prefijo}-`))
      .map((p) => {
        const nombreRemoto = nombreRemotoPorCodigo.get(p.codigo)
        if (p.sincronizado === 1 && nombreRemoto !== undefined && nombreRemoto !== p.nombre) {
          return { ...p, nombre: nombreRemoto }
        }
        return p
      })
    this.proveedores.set(filtrados)

    // Combinamos conteos remotos y locales para mostrar el avance real del dispositivo.
    const remotoPorProveedor: Record<string, number> = {}
    for (const prod of productosRemotos) remotoPorProveedor[prod.proveedorCodigo] = (remotoPorProveedor[prod.proveedorCodigo] ?? 0) + 1

    const conteosLocales = await Promise.all(filtrados.map((p) => contarProductosDeProveedor(p.codigo)))
    const c: Record<string, number> = {}
    filtrados.forEach((p, i) => {
      c[p.codigo] = Math.max(conteosLocales[i], remotoPorProveedor[p.codigo] ?? 0)
    })
    this.conteos.set(c)
    this.pendientes.set(pend)
  }

  cambiarPrefijo(letra: string): void {
    this.prefijo.set(letra)
    guardarPrefijoPersona(letra)
    void this.recargar()
  }

  async nuevoProveedor(): Promise<void> {
    const codigo = await generarCodigoProveedor()
    // Nombre null: todavia no existe, se completa en nuevo-proveedor.ts
    // (guardarYCapturar llama a actualizarProveedorNombre tras guardar).
    this.sesion.iniciarProveedor(codigo, null)
    await this.router.navigateByUrl('/movil/proveedor')
  }

  async continuarProveedor(codigo: string): Promise<void> {
    // Calculamos el siguiente producto desde lo local para no repetir IDs.
    const siguiente = await siguienteNumeroProducto(codigo)
    const nombre = this.proveedores().find((p) => p.codigo === codigo)?.nombre ?? null
    this.sesion.iniciarProveedor(codigo, nombre, siguiente)
    await this.router.navigateByUrl('/movil/captura')
  }

  async sincronizar(): Promise<void> {
    if (!this.sesionSupabaseReal()) return
    this.sincronizando.set(true)
    try {
      await sincronizarAhora()
      await this.recargar()
    } finally {
      this.sincronizando.set(false)
    }
  }

  /** Boton "Ver tablero" — va directo al dashboard sin recargar (ver app.routes.ts). */
  async verTablero(): Promise<void> {
    await this.router.navigateByUrl('/dashboard-movil')
  }

  async cerrarSesion(): Promise<void> {
    this.cerrandoSesion.set(true)
    try {
      this.sesion.limpiarProveedor()
      await this.auth.cerrarSesion()
      await this.router.navigateByUrl('/login', { replaceUrl: true })
    } finally {
      this.cerrandoSesion.set(false)
    }
  }

  async abrirDetalleProductos(codigo: string): Promise<void> {
    const [locales, remotos] = await Promise.all([
      listarProductosLocalDeProveedor(codigo),
      conTimeout(listarProductos(), 2500, []),
    ])
    const mapa = new Map<string, ProductoDetalleItem>()

    for (const p of locales) {
      mapa.set(p.productoId, {
        id: p.productoId,
        mueble: p.mueble || '',
        telas: p.telas.length,
        sincronizado: p.sincronizado === 1,
        telasDetalle: p.telas.map((t) => ({
          id: t.id,
          muestrario: t.muestrario,
          colores: t.colores.join(', '),
          tipo: t.tipo ?? '',
        })),
      })
    }
    for (const p of remotos.filter((prod) => prod.proveedorCodigo === codigo)) {
      mapa.set(p.id, {
        id: p.id,
        // Priorizamos el nombre comercial completado desde el dashboard.
        mueble: p.nombreComercial || p.mueble || '',
        telas: p.telas.length,
        sincronizado: true,
        telasDetalle: p.telas.map((t) => ({
          id: t.id,
          muestrario: t.muestrario,
          colores: t.colores,
          tipo: t.tipo,
        })),
      })
    }

    this.detalleProveedor.set(codigo)
    this.detalleProductos.set(
      [...mapa.values()].sort((a, b) => a.id.localeCompare(b.id, 'es', { numeric: true, sensitivity: 'base' })),
    )
    this.detalleAbierto.set(true)
  }

  cerrarDetalleProductos(): void {
    this.detalleAbierto.set(false)
    this.detalleProveedor.set(null)
    this.detalleProductos.set([])
  }

  /** Acceso rapido a la tarjeta desde la lista -- no dispara continuarProveedor(). */
  abrirTarjetaRapida(p: ProveedorPendiente, evento: Event): void {
    evento.stopPropagation()
    this.tarjetaRapida.set(p)
  }

  cerrarTarjetaRapida(): void {
    this.tarjetaRapida.set(null)
  }

  abrirEdicionNombre(p: ProveedorPendiente, evento: Event): void {
    evento.stopPropagation() // no disparar tambien continuarProveedor()
    this.editandoProveedor.set(p)
    this.nombreEditado.set(p.nombre ?? '')
  }

  cerrarEdicionNombre(): void {
    this.editandoProveedor.set(null)
    this.nombreEditado.set('')
  }

  async guardarNombreEditado(): Promise<void> {
    const p = this.editandoProveedor()
    if (!p) return
    this.guardandoNombre.set(true)
    try {
      await actualizarNombreProveedor(p.codigo, this.nombreEditado().trim())
      this.cerrarEdicionNombre()
      await this.recargar()
    } finally {
      this.guardandoNombre.set(false)
    }
  }

  abrirBorradoProveedor(p: ProveedorPendiente, evento: Event): void {
    evento.stopPropagation() // no disparar tambien continuarProveedor()
    this.borrandoProveedor.set(p)
  }

  cerrarBorradoProveedor(): void {
    this.borrandoProveedor.set(null)
  }

  async confirmarBorradoProveedor(): Promise<void> {
    const p = this.borrandoProveedor()
    if (!p) return
    this.eliminando.set(true)
    try {
      await eliminarProveedor(p.codigo)
      this.cerrarBorradoProveedor()
      await this.recargar()
    } finally {
      this.eliminando.set(false)
    }
  }
}
